// EndpointsManager（Phase 1 #4）—— 管理 API 的版本契约。
//
// 这一段对应 RapidAPI Studio 里的 "Endpoints" tab：技术契约（baseUrl + 认证
// + endpoints）整体作为一个版本被发布、回滚。
//
// 保存：单一「保存」动作 → resolveEditTarget() 决定落到工作副本(已上线 API 的
// copy-on-write 沙箱)还是当前版本(草稿/沙箱就地改)，再 apiSvc.updateVersion。
// 上线 / 回滚 / 新建大版本都在「版本」段，不在这里。
//
// OpenAPI / AI 导入走 mergeImportedEndpoints，按 method+path 合并到现有
// endpoints，业务字段（计费 / hidden / privateHeaders 等）保留。

import React, { useEffect, useRef, useState } from 'react';
import { UnreachableErrorList } from './unreachable-errors.jsx';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { AiImportDialog, Menu, MenuItem, MenuNote } from '../../ui/index.js';
import {
  INITIAL_FORM,
  INITIAL_ENDPOINT,
  apiFormSchema,
  apiDraftFormSchema,
  transformApiToForm,
  buildVersionPayload,
  EndpointsSection,
  BatchPriceDialog,
  DirectModeNotice,
} from '../register.jsx';
import { mergeImportedEndpoints, importedTransportSpecInput } from './openapi-merge.js';

// react-hook-form 把校验错误存成镜像字段结构的嵌套对象，叶子节点形如
// { message, type, ref }。这里递归拍平成 [{ path, message }]，让「需修正」清单
// 能精确告诉用户是哪个字段、什么错（之前只显示端点名，用户无从排查）。
function flattenErrors(node, prefix = '') {
  if (!node || typeof node !== 'object') return [];
  const out = [];
  if (typeof node.message === 'string' && node.message) {
    out.push({ path: prefix, message: node.message });
  }
  for (const [k, v] of Object.entries(node)) {
    if (k === 'message' || k === 'type' || k === 'ref') continue;
    if (v && typeof v === 'object') {
      out.push(...flattenErrors(v, prefix ? `${prefix}.${k}` : k));
    }
  }
  return out;
}

// 字段路径 → 中文友好标签。命中规则给人话，未命中回退原始路径（仍比没有强）。
function prettyFieldPath(path) {
  const tier = path.match(/^aiConfig\.modelPricing\.(\d+)\.tiers\.(\d+)\.(.+)$/);
  if (tier) {
    const f = { inputTokenThreshold: '阈值', inputPricePerToken: '输入价', outputPricePerToken: '输出价', cacheCreationPricePerToken: '缓存写价', cacheReadPricePerToken: '缓存读价' }[tier[3]] || tier[3];
    return `模型定价 第${+tier[1] + 1}行 · 分层${+tier[2] + 1} · ${f}`;
  }
  const mp = path.match(/^aiConfig\.modelPricing\.(\d+)\.(.+)$/);
  if (mp) {
    const f = { modelName: '模型名', inputPricePerToken: '输入价', outputPricePerToken: '输出价', cacheCreationPricePerToken: '缓存写价', cacheReadPricePerToken: '缓存读价' }[mp[2]] || mp[2];
    return `模型定价 第${+mp[1] + 1}行 · ${f}`;
  }
  if (path === 'aiConfig.modelPricing') return '模型定价';
  const tp = path.match(/^aiConfig\.tokenJsonPaths\.(.+)$/);
  if (tp) return `Token 路径 · ${tp[1]}`;
  return ({
    method: '方法', path: '路径', name: '名称', description: '描述',
    billingType: '计费类型', costPerCall: '每次调用价', costPerResource: '每资源价',
    upstreamProvider: '上游 provider', servedModel: '服务模型', fixedHoldAmount: '固定预扣',
  })[path] || path;
}
import { PublishDialog } from './version-shared.jsx';

export const EndpointsManager = ({ api, selectedMajor, inReview, onApiRefreshed, onDirtyChange }) => {
  const { toast } = XPrim.useToast();
  const openapiInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(null);
  const [publishState, setPublishState] = useState(null); // { major, workingId }
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [batchPriceOpen, setBatchPriceOpen] = useState(false);
  // 初始选中:若有跨段跳转目标(从「提审前检查」点端点过来),首屏就选它 —— 用 useState 初始化器
  // 在任何 effect/默认选第一个之前就定下来,彻底避开「默认选第一个」抢先覆盖的竞态。
  const [activeEndpointId, setActiveEndpointId] = useState(() => {
    const t = sessionStorage.getItem('xapi.jumpEndpoint');
    if (t) { sessionStorage.removeItem('xapi.jumpEndpoint'); return t; }
    return null;
  });

  // 未上线(草稿/沙箱)用宽松 schema:AI 端点可缺模型边配边存;已上线(ACTIVE/APPROVED)严格。
  // 上架前的完整性由「提交审核」+ 后端收口。
  const isLive = api?.status === 'ACTIVE' || api?.status === 'APPROVED';
  const methods = useForm({
    resolver: zodResolver(isLive ? apiFormSchema : apiDraftFormSchema),
    defaultValues: api ? transformApiToForm(api) : INITIAL_FORM(),
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { isDirty, errors },
  } = methods;

  // 端点集合的身份签名。只看 id 列表，不看字段内容 —— 目的是识别「服务端多出/少了
  // 端点」，而不是跟着每次编辑重算。
  // 端点的来源必须和 transformApiToForm 一致（register.jsx:804/819）：
  // currentVersion || version，再回落到服务级 endpoints。用错字段就永远监听不到变化。
  const serverEndpoints =
    (api?.currentVersion || api?.version)?.endpoints || api?.endpoints || [];
  const serverEndpointIds = serverEndpoints.map((endpoint) => endpoint.id).join(',');
  const serverFormKey = `${api?.id || ''}:${api?.activeVersionId || api?.activeVersion?.id || ''}:${serverEndpointIds}`;
  const hydratedServerFormKeyRef = useRef(null);

  useEffect(() => {
    // A local edit flips isDirty from false → true. That state transition must
    // never be treated as fresh server data: resetting here remounts the whole
    // endpoint editor, drops input focus and restores the old API value after
    // the very first keystroke. Only hydrate a clean form when the server-side
    // service/version/endpoint identity has actually changed.
    if (!api || isDirty || hydratedServerFormKeyRef.current === serverFormKey) return;
    reset(transformApiToForm(api));
    hydratedServerFormKeyRef.current = serverFormKey;
    // 依赖里必须带上端点签名：只盯 api.id 的话，「自动配置」往同一个服务里写完端点后
    // id 没变，effect 不触发，表单还停在空列表 —— 页面显示 ENDPOINTS · 0，得手动刷新
    // 才看得见。isDirty 时不重灌，避免把用户没保存的编辑冲掉。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isDirty, reset, serverFormKey]);

  // 未保存状态上报外壳(切 tab 拦截提醒,见 workspace-api)。
  useEffect(() => {
    onDirtyChange?.({ isDirty, hasErrors: Object.keys(errors).length > 0, save: () => saveAndReport() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, errors, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(null), [onDirtyChange]);

  const endpointsWatch = watch('endpoints');

  // 租赁组的 CREATE / TERMINATE / STATUS 唯一性是跨端点校验。只重校验刚修改的
  // role 字段会让 resolver 挂在另一个端点上的旧错误残留，进而把保存按钮永久锁住。
  // 仅在租赁拓扑变化时重跑 endpoints，避免普通名称/描述输入触发整表校验。
  const rentalTopologySignature = (endpointsWatch || [])
    .map((ep) => `${ep?.billingType || ''}:${ep?.rentalConfig?.rentalKey || ''}:${ep?.rentalConfig?.role || ''}`)
    .join('|');
  useEffect(() => {
    if (!isDirty) return;
    void trigger('endpoints');
  }, [isDirty, rentalTopologySignature, trigger]);

  // endpoint 集就绪后:当前已选且仍存在 → 保持(含 useState 初始化的跳转目标);
  // 否则(没选 / 选了个已不存在的)→ 默认选第一个,避免右侧详情空白。不覆盖有效选择。
  useEffect(() => {
    if (!endpointsWatch?.length) return;
    if (activeEndpointId && endpointsWatch.some((e) => e.id === activeEndpointId)) return;
    setActiveEndpointId(endpointsWatch[0].id);
  }, [endpointsWatch, activeEndpointId]);

  const accessMode = watch('accessMode');
  const isDirect = accessMode === 'DIRECT';
  const versionId = api?.activeVersionId || api?.activeVersion?.id;
  const serviceStatus = api?.status;
  const errorCount = Object.keys(errors).length;
  // 端点级问题清单:红=硬错(阻断保存),黄=AI 缺模型(草稿可存、上架前必补)。点击跳到第一个。
  const aiNeedsModel = (e) =>
    (e.aiInterfaceSpecId && e.billingType !== 'PER_TOKEN' && !(e.servedModel && String(e.servedModel).trim())) ||
    (e.billingType === 'PER_TOKEN' && !(e.aiConfig?.modelPricing && e.aiConfig.modelPricing.length));
  const epErrorIdxs = (endpointsWatch || []).map((e, i) => (errors.endpoints?.[i] ? i : -1)).filter((i) => i >= 0);
  const epWarnIdxs = (endpointsWatch || []).map((e, i) => (!errors.endpoints?.[i] && aiNeedsModel(e) ? i : -1)).filter((i) => i >= 0);
  const jumpToEndpoint = (idx) => { const ep = (endpointsWatch || [])[idx]; if (ep?.id) setActiveEndpointId(ep.id); };
  const busy = submitting != null;
  const serviceSuspended = serviceStatus === 'SUSPENDED';
  const publishDisabled =
    serviceSuspended || inReview || busy || errorCount > 0 || selectedMajor == null;
  // 两个保存动作的禁用条件 + 禁用原因(给 tooltip 用,让用户知道为什么点不动)。
  const hotfixDisabled = busy || !isDirty || errorCount > 0 || !versionId;
  const releaseDisabled = busy || errorCount > 0;
  const blockReason =
    errorCount > 0
      ? `有 ${errorCount} 个字段需要修正,无法保存`
      : !isDirty
        ? '没有未保存的改动'
        : '';

  // ── 保存动作 ────────────────────────────────────────────────────────────
  // 版本管理已提到「版本」段。这里的保存解析「编辑目标版本」：
  //   已上线 API → 工作副本(copy-on-write，沙箱)，线上不动；草稿/沙箱 → 当前版本就地改。
  const resolveEditTarget = async () => {
    try {
      const ov = await XApi.apiSvc.versionOverview(api.id);
      const major =
        ov?.majors?.find((m) => m.major === selectedMajor) ||
        ov?.majors?.find((m) => m.isDefault) ||
        ov?.majors?.[0];
      if (major?.working) return major.working.id;
      if (major?.live) return (await XApi.apiSvc.startEdit(api.id, major.major)).id;
    } catch { /* 回退到当前版本 */ }
    return versionId;
  };

  const onSave = handleSubmit(
    async (form) => {
      if (!api?.id) return;
      setSubmitting('save');
      try {
        const target = await resolveEditTarget();
        await XApi.apiSvc.updateVersion(api.id, target, buildVersionPayload(form));
        toast({ title: '已保存', type: 'success' });
        // 标记已保存但不重新拉取（已上线 API 改的是工作副本，重拉会显示线上版本）。
        reset(form);
      } catch (e) {
        toast({ title: e?.message || '保存失败', type: 'error' });
      } finally {
        setSubmitting(null);
      }
    },
    () => toast({ title: '请先修正高亮字段', type: 'error' }),
  );

  // 发布：先把当前未保存改动存进所选大版本的工作副本，再弹「改了什么 · 发布到 v{N}」确认上线。
  const onPublish = handleSubmit(
    async (form) => {
      if (!api?.id || selectedMajor == null) return;
      if (serviceSuspended) {
        toast({
          title: '服务已由管理员暂停；请联系管理员恢复，无需重新提交审核',
          type: 'error',
        });
        return;
      }
      setSubmitting('publish');
      try {
        const target = await resolveEditTarget();
        if (isDirty) await XApi.apiSvc.updateVersion(api.id, target, buildVersionPayload(form));
        if (!target) throw new Error('找不到要发布的版本');
        reset(form); // 已落库，清 dirty
        setPublishState({ major: selectedMajor, workingId: target });
      } catch (e) {
        toast({ title: e?.message || '发布准备失败', type: 'error' });
      } finally {
        setSubmitting(null);
      }
    },
    () => toast({ title: '请先修正高亮字段', type: 'error' }),
  );

  // 给「保存并离开」用:落进编辑目标版本(工作副本/当前版本),返回是否成功。
  const saveAndReport = () =>
    new Promise((resolve) => {
      if (!api?.id || !versionId) { resolve(true); return; }
      methods.handleSubmit(
        async (form) => {
          setSubmitting('save');
          try {
            const target = await resolveEditTarget();
            await XApi.apiSvc.updateVersion(api.id, target, buildVersionPayload(form));
            toast({ title: '已保存', type: 'success' });
            reset(form);
            resolve(true);
          } catch (e) {
            toast({ title: e?.message || '更新失败', type: 'error' });
            resolve(false);
          } finally {
            setSubmitting(null);
          }
        },
        () => { toast({ title: '请先修正高亮字段', type: 'error' }); resolve(false); },
      )();
    });

  // ── 导入：OpenAPI 文件 ──────────────────────────────────────────────────
  const onImportOpenApi = async (file) => {
    if (!file) return;
    setSubmitting('parse');
    try {
      const text = await file.text();
      const parsed = await XApi.apiSvc.parseOpenApi(text);
      if (!parsed) throw new Error('Empty response from parser');
      const incomingEps = Array.isArray(parsed.endpoints) ? parsed.endpoints : [];

      const cur = getValues();
      const merged = mergeImportedEndpoints(cur.endpoints, incomingEps, INITIAL_ENDPOINT);

      // 用 setValue + shouldDirty 而不是 reset：reset 即使带 keepDirty 也只
      // 保留 *原本* 的 dirty 状态——如果导入前表单是 clean 的，导入后还是
      // clean，Hotfix 按钮就被卡住。用 setValue 显式标记 dirty 让保存按钮亮起来。
      setValue('endpoints', merged, { shouldDirty: true });
      const openApiSpec = importedTransportSpecInput(parsed.openApiSpec, cur.openApiSpec);
      if (openApiSpec) setValue('openApiSpec', openApiSpec, { shouldDirty: true });
      if (!cur.baseUrl && parsed.baseUrl) {
        setValue('baseUrl', parsed.baseUrl, { shouldDirty: true });
      }
      if (
        parsed.authType &&
        parsed.authType !== 'NONE' &&
        (!cur.authType || cur.authType === 'NONE')
      ) {
        setValue('authType', parsed.authType, { shouldDirty: true });
        if (parsed.authConfig && typeof parsed.authConfig === 'object') {
          setValue(
            'authConfig',
            { key: parsed.authConfig.key || '', value: parsed.authConfig.value || '' },
            { shouldDirty: true },
          );
        }
      }

      toast({ title: `Imported ${incomingEps.length} endpoint${incomingEps.length === 1 ? '' : 's'}`, type: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'OpenAPI parse failed', type: 'error' });
    } finally {
      setSubmitting(null);
      if (openapiInputRef.current) openapiInputRef.current.value = '';
    }
  };

  // ── 导入：AI Analysis ───────────────────────────────────────────────────
  // AI 弹窗分析完成后会把结果塞到 sessionStorage，这里轮询一次并 merge。
  // (register.jsx 也有同样的 handoff，但它走的是 full-reset；在工作区编辑场景
  //  我们要求保留已有业务字段，所以这里独立处理。)
  const consumeAiHandoff = () => {
    let stash;
    try {
      const raw = sessionStorage.getItem('xapi.aiImport.session');
      if (!raw) return;
      stash = JSON.parse(raw);
      sessionStorage.removeItem('xapi.aiImport.session');
    } catch { return; }
    const info = stash?.resp?.extractedInfo;
    if (!info) return;
    const eps = Array.isArray(info.endpoints) ? info.endpoints : [];
    if (eps.length === 0) {
      toast({ title: 'AI analysis returned no endpoints', type: 'info' });
      return;
    }
    const cur = getValues();
    const merged = mergeImportedEndpoints(cur.endpoints, eps, INITIAL_ENDPOINT);
    setValue('endpoints', merged, { shouldDirty: true });
    const openApiSpec = importedTransportSpecInput(stash?.resp?.openApiSpec, cur.openApiSpec);
    if (openApiSpec) setValue('openApiSpec', openApiSpec, { shouldDirty: true });
    toast({ title: `Merged ${eps.length} endpoint${eps.length === 1 ? '' : 's'} from AI`, type: 'success' });
  };

  const onAiDialogClose = () => {
    setAiImportOpen(false);
    // Dialog 关闭后立即检查 sessionStorage —— AI 完成时会写入
    setTimeout(consumeAiHandoff, 50);
  };

  if (!api) {
    return <div style={{ padding: 32, color: 'var(--fg-2)' }}>API not loaded</div>;
  }

  return (
    <FormProvider {...methods}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        {/* Sticky top bar —— top:52 让它贴在全局 TopBar(高 52)下方,不被盖住 */}
        <div
          style={{
            position: 'sticky',
            top: 52,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 32px',
            background: 'var(--bg-0)',
            borderBottom: '1px solid var(--bd-1)',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 500,
                color: 'var(--fg-0)',
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span>管理 API</span>
              <StatusBadge status={serviceStatus} />
            </h2>
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
              技术契约（上游 + 端点）· 保存进沙箱工作副本；点「发布」上线。版本切换在左上角
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* 未保存指示:visibility 切换、空间常驻 —— 出现/消失不会让按钮上下/左右跳。 */}
            <span
              style={{
                fontSize: 11,
                color: 'var(--warn)',
                whiteSpace: 'nowrap',
                marginRight: 2,
                visibility: isDirty ? 'visible' : 'hidden',
              }}
            >
              ● 未保存
            </span>
            <input
              ref={openapiInputRef}
              type="file"
              accept=".json,.yaml,.yml,.txt,application/json,application/x-yaml"
              style={{ display: 'none' }}
              onChange={(e) => onImportOpenApi(e.target.files && e.target.files[0])}
            />
            <SecondaryBtn
              onClick={() => openapiInputRef.current && openapiInputRef.current.click()}
              disabled={busy}
              icon={XIcons.IconDownload}
              label={submitting === 'parse' ? '解析中…' : '导入 OpenAPI'}
            />
            <SecondaryBtn
              onClick={() => setAiImportOpen(true)}
              disabled={busy}
              icon={XIcons.IconZap}
              label="AI 导入"
            />
            {/* 工具 ▾：Base UI Menu —— 先选具体工具 → 打开对应弹窗（目前：批量改价） */}
            <Menu
              disabled={busy || isDirect}
              title={isDirect ? 'DIRECT 模式无批量工具' : '批量工具'}
              minWidth={220}
              triggerStyle={{
                padding: '8px 12px', borderRadius: 6, border: '1px solid var(--bd-1)',
                background: 'var(--bg-0)', color: (busy || isDirect) ? 'var(--fg-3)' : 'var(--fg-1)',
                fontSize: 12, cursor: (busy || isDirect) ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
              trigger={<><XIcons.IconSettings size={12} /><span>工具 ▾</span></>}
            >
              <MenuItem icon={XIcons.IconDollar} onClick={() => setBatchPriceOpen(true)}>批量改价（PER_CALL）</MenuItem>
              <MenuNote>更多工具开发中…</MenuNote>
            </Menu>
            {/* 端点问题 —— 紧挨保存按钮:点开是完整清单(哪几个端点 + 什么问题),
                点某一项直接跳到该端点。🔴 红=硬错(阻断保存) / 🟡 黄=AI 待配模型(草稿可存,上架前必补)。 */}
            {epErrorIdxs.length > 0 && (
              <Menu
                minWidth={300}
                triggerStyle={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(192,57,43,0.55)', background: 'rgba(192,57,43,0.12)', color: '#e06b5e', fontSize: 12, fontWeight: 500 }}
                trigger={<><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--err)', flexShrink: 0 }} />{epErrorIdxs.length} 个端点需修正<XIcons.IconChevronDown size={11} /></>}
              >
                <MenuNote>这些端点有校验错误,未修正无法保存（点击跳转）：</MenuNote>
                {epErrorIdxs.map((idx) => {
                  const ep = (endpointsWatch || [])[idx];
                  const fieldErrs = flattenErrors(errors.endpoints?.[idx]);
                  return (
                    <div key={idx}>
                      <MenuItem icon={XIcons.IconCode} onClick={() => jumpToEndpoint(idx)}>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{ep?.method}</span>{' '}
                        {ep?.name || ep?.path || '#' + (idx + 1)}
                      </MenuItem>
                      {fieldErrs.length > 0 && (
                        <div style={{ padding: '2px 12px 8px 30px' }}>
                          {fieldErrs.map((fe, j) => (
                            <div key={j} style={{ fontSize: 11, color: '#e06b5e', lineHeight: 1.5, display: 'flex', gap: 6 }}>
                              <span style={{ flexShrink: 0 }}>·</span>
                              <span><b style={{ fontWeight: 600 }}>{prettyFieldPath(fe.path)}</b>：{fe.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Menu>
            )}
            {/* 兜底：校验拦住了保存、但页面上找不到对应输入框的字段。正常情况下恒为空；
                非空说明校验规则和渲染分支脱节了（本仓库已出过三次），至少让人看得见。 */}
            <UnreachableErrorList errors={errors} />
            {epWarnIdxs.length > 0 && (
              <Menu
                minWidth={300}
                triggerStyle={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(224,142,11,0.5)', background: 'rgba(224,142,11,0.12)', color: 'var(--warn, #e08e0b)', fontSize: 12, fontWeight: 500 }}
                trigger={<><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--warn, #e08e0b)', flexShrink: 0 }} />{epWarnIdxs.length} 个待配模型<XIcons.IconChevronDown size={11} /></>}
              >
                <MenuNote>这些 AI 端点上架前必须配置服务模型（点击跳到该端点的「模型」配置）：</MenuNote>
                {epWarnIdxs.map((idx) => {
                  const ep = (endpointsWatch || [])[idx];
                  return (
                    <MenuItem key={idx} icon={XIcons.IconZap} onClick={() => jumpToEndpoint(idx)}>
                      <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{ep?.method}</span>{' '}
                      {ep?.name || ep?.path || '#' + (idx + 1)}
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
            {/* 分隔:导入工具 ↔ 保存/发布 */}
            <div style={{ width: 1, height: 20, background: 'var(--bd-2, rgba(255,255,255,0.12))', margin: '0 2px' }} />
            {selectedMajor != null && (
              inReview ? (
                <span style={{ fontSize: 11, color: '#2563eb', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  <span className="ws-spin" style={{ width: 9, height: 9, borderRadius: '50%', border: '2px solid #2563eb', borderTopColor: 'transparent', display: 'inline-block', marginRight: 5 }} />
                  v{selectedMajor} 审核中
                </span>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}>
                  正在编辑 <b style={{ color: 'var(--fg-1)' }}>v{selectedMajor}</b>
                </span>
              )
            )}
            {/* 保存 → 所选大版本工作副本（沙箱，线上不动）；发布 → 弹改动确认后上线。审核中锁住。 */}
            <button
              type="button"
              onClick={onSave}
              disabled={hotfixDisabled || inReview}
              title={inReview ? '审核中，暂不可编辑' : hotfixDisabled ? (blockReason || '保存') : `保存到 v${selectedMajor ?? ''}（工作副本/沙箱，不影响线上）`}
              style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid var(--bd-1)',
                background: (hotfixDisabled || inReview) ? 'var(--bg-1)' : 'var(--bg-0)',
                color: (hotfixDisabled || inReview) ? 'var(--fg-4)' : 'var(--fg-0)',
                fontSize: 13, fontWeight: 600,
                cursor: (hotfixDisabled || inReview) ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting === 'save' ? '保存中…' : '保存'}
            </button>
            <button
              type="button"
              onClick={onPublish}
              disabled={publishDisabled}
              title={serviceSuspended ? '服务已由管理员暂停；恢复由管理员操作，无需重新提交审核' : inReview ? '已提交，审核中…' : errorCount > 0 ? blockReason : `发布到 v${selectedMajor ?? ''}（确认前会显示改动）`}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: publishDisabled ? 'var(--bg-1)' : 'var(--fg-0)',
                color: publishDisabled ? 'var(--fg-4)' : 'var(--bg-0)',
                fontSize: 13, fontWeight: 600,
                cursor: publishDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {inReview ? '审核中…' : submitting === 'publish' ? '准备中…' : '发布'}
            </button>
          </div>
        </div>

        {/* 钉成一屏:无顶距/底距,让 master/detail 直接贴在工具栏下沿 —— 泳道右边线
            与工具栏底边线相连,不留缺口(见用户反馈);泳道头固定、详情内部滚动。 */}
        <div style={{ padding: '0 32px' }}>
          {/* baseUrl / auth / private headers 已经搬到 basic 段的"上游接入" tab。
             这里 apis 段聚焦 endpoints 列表本身。DIRECT 模式下提示一下，
             因为 endpoints 列表里 cost / 计费 等字段在 DIRECT 模式下会被
             强制 PER_CALL（见 transformEndpointToApi）。*/}
          {isDirect && (
            <div style={{ margin: '16px 0' }}>
              <DirectModeNotice />
            </div>
          )}
          {/* full-bleed: 抵消外层 32px 横向 padding,让 master/detail 的工具栏下边线
              + 列表右边线一直连到左侧竖分割线(#18,不留缺口);padX=32 把内容回推,
              所以线满铺、文字仍有边距。 */}
          <div style={{ margin: '0 -32px' }}>
            <EndpointsSection
              activeEndpointId={activeEndpointId}
              setActiveEndpointId={setActiveEndpointId}
              accessMode={accessMode}
              // 顶部 sticky 已有"导入 OpenAPI"按钮——传 null 让 EndpointsSection
              // 内部那个重复入口隐藏，避免两个相同动作并存。
              onImportClick={null}
              parsing={submitting === 'parse'}
              padX={32}
              // 全局 TopBar(52) + 本段 sticky 工具栏(~70) → 泳道贴在 122 处常驻
              listStickyTop={122}
            />
          </div>
        </div>

        <AiImportDialog
          open={aiImportOpen}
          onClose={onAiDialogClose}
          onAnalyzed={onAiDialogClose}
        />

        {/* 工具 · 批量改价独立弹窗（弹窗内勾选接口/分组） */}
        <BatchPriceDialog open={batchPriceOpen} onClose={() => setBatchPriceOpen(false)} />

        <PublishDialog
          open={!!publishState}
          apiId={api?.id}
          major={publishState?.major}
          workingId={publishState?.workingId}
          onClose={() => setPublishState(null)}
          onPublished={() => { setPublishState(null); onApiRefreshed?.(); }}
        />
      </div>
    </FormProvider>
  );
};

const SecondaryBtn = ({ onClick, disabled, icon: Icon, label, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      padding: '8px 12px',
      borderRadius: 6,
      border: '1px solid var(--bd-1)',
      background: 'var(--bg-0)',
      color: disabled ? 'var(--fg-3)' : 'var(--fg-1)',
      fontSize: 12,
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    }}
  >
    {Icon && <Icon size={12} />}
    <span>{label}</span>
  </button>
);

// 小尺寸状态 pill —— 与 RegisterPage.FormHeader 里的样式同构但更紧凑。
const STATUS_COLOR = {
  ACTIVE: '#22a06b',
  DRAFT: 'var(--fg-3)',
  SANDBOX: '#e08e0b',
  PENDING_REVIEW: '#e08e0b',
  REJECTED: '#c0392b',
  SUSPENDED: '#c0392b',
  DELETED: 'var(--fg-3)',
};
const StatusBadge = ({ status }) => {
  if (!status) return null;
  const color = STATUS_COLOR[status] || 'var(--fg-3)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--bg-1)',
        border: '1px solid var(--bd-1)',
        fontSize: 10,
        color: 'var(--fg-2)',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: color,
        }}
      />
      <span className="mono" style={{ color }}>
        {status.toLowerCase()}
      </span>
    </span>
  );
};
